import { useQuery } from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { ApiError } from "@/lib/api";
import { autodeskKeys } from "@/lib/query-keys";
import type {
  AutodeskConnection,
  AutodeskPushStatus,
  AutodeskRobot,
  FormaProjectListResponse,
  RecordAutodeskPush,
} from "@/types/api";

const AUTODESK_PUSH_POLL_INTERVAL_MS = 3_000;
const IN_FLIGHT_PUSH_STATUSES: ReadonlySet<AutodeskPushStatus> = new Set([
  "pending",
  "creating",
  "attaching",
]);

/**
 * The `refetchInterval` policy for `useRecordAutodeskPush`: polls every 3s
 * while a push is in flight, stops once it reaches a terminal status
 * (`completed`/`failed`) or there's no row yet (never pushed / not loaded).
 * Extracted as a pure function (mirrors `tilePollInterval` in
 * `plans-view/use-project-plans.ts`) so it's unit-testable directly against
 * a bare status value, instead of only through a timer-based test against
 * the live query.
 */
export function autodeskPushPollInterval(
  status: AutodeskPushStatus | undefined,
): number | false {
  if (status && IN_FLIGHT_PUSH_STATUSES.has(status)) {
    return AUTODESK_PUSH_POLL_INTERVAL_MS;
  }
  return false;
}

/**
 * `GET .../autodesk-connection/` returns 404 when the project has no
 * connection configured yet — an ordinary, expected state (not "unpinned"
 * / "still configuring"). Re-throwing it as a query error would put the
 * panel in an error state on first load for every project that hasn't set
 * up Autodesk; catching it here surfaces `data: null` instead so the panel
 * can render its "not connected" state directly off `data`.
 */
export function useAutodeskConnection(projectId: string, enabled = true) {
  const fetchWithAuth = useAuthenticatedFetch();
  return useQuery({
    queryKey: autodeskKeys.connection(projectId),
    queryFn: async () => {
      try {
        return await fetchWithAuth<AutodeskConnection>(
          `/projects/2.0/projects/${projectId}/autodesk-connection/`,
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled,
    retry: false, // a 404 (no connection) is an expected, non-retryable state
  });
}

/**
 * `GET .../autodesk-robot/` returns 404 when the viewer's organization has
 * no Autodesk robot minted — i.e. the integration isn't enabled for the
 * org at all. Like `useAutodeskConnection` above, that's an ordinary,
 * expected state (most orgs won't have the integration), so it resolves to
 * `data: null` rather than a query error; the settings panel keys its
 * "not enabled for your company" state off `data === null` OR a resolved
 * robot with `is_active: false` (staff has disabled a previously-provisioned
 * robot, e.g. offboarding an org without deleting its identity). Note this
 * 404 answers a different question than the connection read's 404:
 * robot 404 = org not enabled, connection 404 = org enabled but this
 * project not linked yet.
 */
export function useAutodeskRobot(projectId: string, enabled = true) {
  const fetchWithAuth = useAuthenticatedFetch();
  return useQuery({
    queryKey: autodeskKeys.robot(projectId),
    queryFn: async () => {
      try {
        return await fetchWithAuth<AutodeskRobot>(
          `/projects/2.0/projects/${projectId}/autodesk-robot/`,
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled,
    retry: false, // a 404 (org not enabled) is an expected, non-retryable state
  });
}

export function useFormaProjects(projectId: string, enabled: boolean) {
  const fetchWithAuth = useAuthenticatedFetch();
  return useQuery({
    queryKey: autodeskKeys.formaProjects(projectId),
    queryFn: () =>
      fetchWithAuth<FormaProjectListResponse>(
        `/projects/2.0/projects/${projectId}/autodesk-forma-projects/`,
      ),
    enabled,
  });
}

/**
 * `GET .../autodesk-push/` returns 404 when the record has never been
 * pushed — the expected default state for the vast majority of records.
 * Caught here and surfaced as `data: null` so the chip can key off
 * `data === null` for "not pushed yet" instead of treating every unpushed
 * record as a query error. A 403 (record not viewable) is NOT caught here —
 * it propagates as a normal query error for Task 9 to render as a
 * disabled/absent affordance.
 */
export function useRecordAutodeskPush(
  projectId: string,
  recordId: string,
  enabled: boolean,
) {
  const fetchWithAuth = useAuthenticatedFetch();
  return useQuery({
    queryKey: autodeskKeys.recordPush(projectId, recordId),
    queryFn: async () => {
      try {
        return await fetchWithAuth<RecordAutodeskPush>(
          `/projects/2.0/projects/${projectId}/records/${recordId}/autodesk-push/`,
        );
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled,
    retry: false, // 404 = never pushed; expected. Other errors also shouldn't retry blindly here — Task 9 owns retry UX.
    // Keeps "Sending…" live and lets a server-side FAILED transition surface
    // Retry without the user having to reopen the record. See
    // `autodeskPushPollInterval` for the interval policy.
    refetchInterval: (query) =>
      autodeskPushPollInterval(query.state.data?.status),
  });
}
